import type {
  ClientOrganization,
  ClientOrganizationData
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { TFunction } from 'i18next';
import type { RefinementCtx } from 'zod';

import type { ActiveExtensionData } from '@/features/profile-setting/components/organization-extensions/fragmentTypes';
import { territorialAuthorityExtension } from '@/features/profile-setting/components/organization-extensions/territorial-authority';
import type {
  OrganizationExtension,
  OrganizationTag
} from '@/features/profile-setting/components/organization-extensions/types';

const EXTENSIONS: OrganizationExtension[] = [territorialAuthorityExtension];

export const getAllExtensions = (): OrganizationExtension[] => EXTENSIONS;

export const getActiveExtensions = (
  tags: OrganizationTag[]
): OrganizationExtension[] =>
  EXTENSIONS.filter((ext) => tags.includes(ext.tag));

export const getActiveExtensionsForOrganization = (
  organization: ClientOrganization
): OrganizationExtension[] => getActiveExtensions(organization.tags ?? []);

export const composeCreateDefaults = (
  extensions: OrganizationExtension[]
): ActiveExtensionData =>
  Object.fromEntries(
    extensions.map((ext) => [ext.dataKey, ext.createDefaults()])
  );

export const composeEditDataDefaults = (
  organization: ClientOrganization,
  extensions: OrganizationExtension[]
): ActiveExtensionData =>
  Object.fromEntries(
    extensions.map((ext) => [ext.dataKey, ext.editDefaults(organization)])
  );

export const mergeDataOnTagChange = (
  current: ActiveExtensionData,
  nextActive: OrganizationExtension[]
): ActiveExtensionData => {
  const next: ActiveExtensionData = {};
  for (const ext of nextActive) {
    const surviving = current[ext.dataKey];
    next[ext.dataKey] =
      surviving === undefined ? ext.createDefaults() : surviving;
  }
  return next;
};

export const composeCreateDataPayload = (
  values: ActiveExtensionData,
  extensions: OrganizationExtension[]
): ClientOrganizationData | undefined => {
  const entries = extensions.flatMap((ext) => {
    const fragment = values[ext.dataKey];
    if (fragment === undefined) {
      return [];
    }
    const payload = ext.toCreatePayload(fragment);
    if (payload === undefined) {
      return [];
    }
    return [[ext.dataKey, payload] as const];
  });
  if (entries.length === 0) {
    return undefined;
  }
  return Object.fromEntries(entries);
};

export const validateExtensionData = (
  values: ActiveExtensionData,
  extensions: OrganizationExtension[],
  t: TFunction<
    'profile-setting',
    'screens.organization-create' | 'screens.organization-edit'
  >,
  ctx: RefinementCtx,
  pathPrefix: (string | number)[] = []
) => {
  for (const ext of extensions) {
    const fragment = values[ext.dataKey];
    if (fragment === undefined) {
      continue;
    }
    const result = ext.schemaFragment(t).safeParse(fragment);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({
          ...issue,
          path: [...pathPrefix, ext.dataKey, ...issue.path]
        });
      }
    }
  }
};

export const composeEditDataPayload = (
  values: ActiveExtensionData,
  extensions: OrganizationExtension[]
): ActiveExtensionData | undefined => {
  if (extensions.length === 0) {
    return undefined;
  }
  const entries = extensions.map((ext) => {
    const fragment = values[ext.dataKey];
    if (fragment === undefined) {
      return [ext.dataKey, undefined] as const;
    }
    return [ext.dataKey, ext.toEditPayload(fragment)] as const;
  });
  return Object.fromEntries(entries);
};
